import type {
  TvCollection,
  TvCreator,
  TvEpisode,
  TvLibraryItem,
  TvSeries,
} from '../types';

const u = (id: string, w = 1600) =>
  `https://images.unsplash.com/photo-${id}?w=${w}&q=80&auto=format&fit=crop`;

const portrait = (id: string, w = 600) =>
  `https://images.unsplash.com/photo-${id}?w=${w}&q=80&auto=format&fit=crop&crop=faces`;

const ep = (
  ordinal: number,
  title: string,
  synopsis: string,
  duration: number,
  stillUrl: string,
  unlock: TvEpisode['unlock'] = 'subscribed',
  premieresAt?: string,
): TvEpisode => ({
  id: `e_${ordinal}_${Math.random().toString(36).slice(2, 8)}`,
  ordinal,
  title,
  synopsis,
  duration,
  unlock,
  stillUrl,
  scene: 'club_room',
  sceneSeed: ordinal * 7,
  premieresAt,
});

/* ============================================================
   CREATORS
   ============================================================ */

export const mockTvCreators: TvCreator[] = [
  {
    id: 'c_kasper',
    handle: 'kasper',
    name: 'KASPER REID',
    role: 'filmmaker',
    bio: 'Long takes, slow rooms. Films the hour after the headliners go home.',
    city: 'Melbourne',
    subscribers: 12_400,
    tags: ['long form', 'documentary', 'late nights'],
    portraitUrl: portrait('1488161628813-04466f872be2'),
    isVerified: true,
  },
  {
    id: 'c_mara',
    handle: 'mara',
    name: 'MARA OKEKE',
    role: 'documentarian',
    bio: 'Portraits and rooms. The book that became a series.',
    city: 'Berlin · Melbourne',
    subscribers: 8_200,
    tags: ['portrait', 'book', 'editorial'],
    portraitUrl: portrait('1534528741775-53994a69daeb'),
    isVerified: true,
  },
  {
    id: 'c_phoenix',
    handle: 'phoenix',
    name: 'PHOENIX TOLU',
    role: 'dj',
    bio: 'Resident DJ turned filmmaker. The booth, then the after.',
    city: 'Sydney · Melbourne',
    subscribers: 21_700,
    tags: ['dj set', 'after hours', 'live'],
    portraitUrl: portrait('1564564321837-a57b7070ac4f'),
    isVerified: true,
  },
  {
    id: 'c_sebastian',
    handle: 'sebastian',
    name: 'SEBASTIAN ROUX',
    role: 'photographer',
    bio: 'Portraits with one light and a long evening.',
    city: 'Melbourne',
    subscribers: 4_600,
    tags: ['portrait', 'flash', 'studio'],
    portraitUrl: portrait('1500648767791-00dcc994a43e'),
    isVerified: true,
  },
  {
    id: 'c_iris',
    handle: 'iris',
    name: 'IRIS LANE',
    role: 'editor',
    bio: 'Cuts the long takes into something quieter and stranger.',
    city: 'Melbourne',
    subscribers: 3_100,
    tags: ['edit', 'short form', 'experimental'],
    portraitUrl: portrait('1492562080023-ab3db95bfbce'),
    isVerified: false,
  },
  {
    id: 'c_jude',
    handle: 'jude',
    name: 'JUDE ARMITAGE',
    role: 'filmmaker',
    bio: 'Shoots dancefloors like they\'re portraits.',
    city: 'Melbourne',
    subscribers: 9_800,
    tags: ['dancefloor', 'doc', 'super 16'],
    portraitUrl: portrait('1531891437562-4301cf35b7e4'),
    isVerified: true,
  },

  /* ===== Performers — working on the roster ===== */
  {
    id: 'c_roman_saint',
    handle: 'roman_saint',
    name: 'ROMAN SAINT',
    role: 'performer',
    bio: 'Long takes. Hotel light. Solo work and selective collaborations.',
    city: 'Sydney · Melbourne',
    subscribers: 31_400,
    tags: ['performer', 'solo', 'hotel cinema'],
    portraitUrl: portrait('1500648767791-00dcc994a43e'),
    isVerified: true,
  },
  {
    id: 'c_alexei_kira',
    handle: 'alexei_kira',
    name: 'ALEXEI KIRA',
    role: 'performer',
    bio: 'Trained dancer. Camera work since 2024. Quiet on set, loud on screen.',
    city: 'Melbourne',
    subscribers: 18_900,
    tags: ['performer', 'dancer', 'studio work'],
    portraitUrl: portrait('1539571696357-5a69c17a67c6'),
    isVerified: true,
  },
  {
    id: 'c_kai_north',
    handle: 'kai_north',
    name: 'KAI NORTH',
    role: 'performer',
    bio: 'Northbound. Touring performer with two studio releases this year.',
    city: 'Auckland · Melbourne',
    subscribers: 24_600,
    tags: ['performer', 'touring', 'studio'],
    portraitUrl: portrait('1531891437562-4301cf35b7e4'),
    isVerified: true,
  },
  {
    id: 'c_cassian_vega',
    handle: 'cassian_vega',
    name: 'CASSIAN VEGA',
    role: 'performer',
    bio: 'Director-performer. Writes the scenes he\'s in.',
    city: 'Melbourne',
    subscribers: 15_300,
    tags: ['performer', 'director', 'long form'],
    portraitUrl: portrait('1568602471122-7832951cc4c5'),
    isVerified: true,
  },

  /* ===== Studios — production-label channels ===== */
  {
    id: 'c_late_light',
    handle: 'late_light',
    name: 'LATE LIGHT STUDIOS',
    role: 'studio',
    bio: 'Premium production. Cinematic adult cinema. Monthly drops, selective performers.',
    city: 'Melbourne',
    subscribers: 47_200,
    tags: ['studio', 'monthly drops', 'cinematic'],
    portraitUrl: portrait('1488161628813-04466f872be2'),
    isVerified: true,
  },
];

const byHandle = (handle: string) =>
  mockTvCreators.find((c) => c.handle === handle)?.id ?? mockTvCreators[0].id;

/* ============================================================
   SERIES
   ============================================================ */

export const mockTvSeries: TvSeries[] = [
  {
    id: 's_mid_tempo',
    slug: 'mid-tempo',
    title: 'MID TEMPO',
    eyebrow: 'Returning · Season 2 in May',
    tagline: 'Slower, weirder, closer.',
    synopsis:
      'Eight new chapters from the rooms after the headliners go home. Long takes, low light, the slowest hour of the night.',
    kind: 'series',
    rating: '18+',
    releasedAt: '2025-11-04',
    creatorIds: [byHandle('kasper'), byHandle('iris')],
    heroUrl: u('1492684223066-81342ee5ff30', 2400),
    portraitUrl: u('1571266028243-d220c6a7d1ab', 1200),
    scene: 'club_room',
    sceneSeed: 17,
    tags: ['original', 'after hours', 'documentary'],
    episodes: [
      ep(1, 'Last Light', 'The lights go up. Everyone knows but no one moves yet.', 28 * 60, u('1518998053901-5348d3961a04')),
      ep(2, 'The Booth', 'Phoenix runs another forty minutes after the official close.', 31 * 60, u('1492684223066-81342ee5ff30')),
      ep(3, 'Out the Side Door', 'Two of them, three streets, no taxi.', 22 * 60, u('1478720568477-152d9b164e26')),
      ep(4, 'Roof', 'Cigarettes and a conversation that doesn\'t want to end.', 26 * 60, u('1518998053901-5348d3961a04'), 'subscribed', '2026-05-22'),
      ep(5, 'The Office', 'Door staff after lockdown. The night between the night and tomorrow.', 24 * 60, u('1488161628813-04466f872be2'), 'locked'),
      ep(6, 'Mirror', 'Hotel bathroom, two friends, one decision.', 19 * 60, u('1571266028243-d220c6a7d1ab'), 'locked'),
    ],
  },
  {
    id: 's_after_the_headliners',
    slug: 'after-the-headliners',
    title: 'AFTER THE HEADLINERS',
    eyebrow: 'Original series',
    tagline: 'The hour the headlines forget.',
    synopsis:
      'Six rooms. Six closing sets. Six rooms after. A documentary cycle by Phoenix Tolu.',
    kind: 'series',
    rating: '15+',
    releasedAt: '2025-08-12',
    creatorIds: [byHandle('phoenix')],
    heroUrl: u('1571266028243-d220c6a7d1ab', 2400),
    scene: 'haze',
    sceneSeed: 23,
    tags: ['original', 'dj', 'documentary'],
    episodes: [
      ep(1, 'The Gordon · 4am', 'A small room. A long set. The bar staff know.', 42 * 60, u('1492684223066-81342ee5ff30'), 'free'),
      ep(2, 'East Brunswick', 'The garage party that became a residency.', 36 * 60, u('1518998053901-5348d3961a04')),
      ep(3, 'Sub-bass', 'Sound system culture in three minutes flat.', 29 * 60, u('1571266028243-d220c6a7d1ab')),
      ep(4, 'Smoke Room', 'The conversation behind the booth.', 34 * 60, u('1488161628813-04466f872be2')),
    ],
  },
  {
    id: 's_room_tone',
    slug: 'room-tone',
    title: 'ROOM TONE',
    eyebrow: 'New short cycle',
    tagline: 'The sound after the music ends.',
    synopsis:
      'Twelve five-minute portraits of empty rooms after closing. Edited by Iris Lane.',
    kind: 'short',
    rating: '15+',
    releasedAt: '2026-04-30',
    creatorIds: [byHandle('iris')],
    heroUrl: u('1478720568477-152d9b164e26', 2400),
    scene: 'mirror',
    sceneSeed: 9,
    tags: ['short form', 'editorial', 'experimental'],
    episodes: [
      ep(1, 'The Gordon, dawn', 'Five minutes after the doors lock.', 5 * 60, u('1478720568477-152d9b164e26'), 'free'),
      ep(2, 'Northcote Social, 5am', 'Glasses on the floor and the heater off.', 5 * 60, u('1492684223066-81342ee5ff30')),
      ep(3, 'A motel mirror', 'A room a guest already left.', 5 * 60, u('1568602471122-7832951cc4c5')),
      ep(4, 'The booth', 'Two needles and a long sigh.', 5 * 60, u('1564564321837-a57b7070ac4f')),
    ],
  },
  {
    id: 's_sweat_capital',
    slug: 'sweat-capital',
    title: 'SWEAT CAPITAL',
    eyebrow: 'Premiering this week',
    tagline: 'A dancefloor, in the round.',
    synopsis:
      'Four nights. Four rooms. One camera in the middle of the floor. By Jude Armitage.',
    kind: 'documentary',
    rating: '18+',
    releasedAt: '2026-05-15',
    creatorIds: [byHandle('jude')],
    heroUrl: u('1492684223066-81342ee5ff30', 2400),
    scene: 'flash',
    sceneSeed: 33,
    tags: ['original', 'dancefloor', 'doc'],
    episodes: [
      ep(1, 'Side A', 'Friday, the early set.', 38 * 60, u('1492684223066-81342ee5ff30'), 'subscribed', '2026-05-22'),
      ep(2, 'Side B', 'Friday, the late set.', 41 * 60, u('1492684223066-81342ee5ff30'), 'subscribed', '2026-05-29'),
      ep(3, 'Side C', 'Saturday, the warm-up.', 33 * 60, u('1564564321837-a57b7070ac4f'), 'locked'),
      ep(4, 'Side D', 'Saturday, the come-down.', 47 * 60, u('1488161628813-04466f872be2'), 'locked'),
    ],
  },
  {
    id: 's_northbound',
    slug: 'northbound',
    title: 'NORTHBOUND',
    eyebrow: 'Limited series',
    tagline: 'The road that made the room.',
    synopsis:
      'Six cities. Five sets. One car. A travel diary that turned into a release.',
    kind: 'series',
    rating: '15+',
    releasedAt: '2026-03-21',
    creatorIds: [byHandle('phoenix'), byHandle('mara')],
    heroUrl: u('1494438639946-1ebd1d20bf85', 2400),
    scene: 'haze',
    sceneSeed: 19,
    tags: ['travel', 'dj', 'doc'],
    episodes: [
      ep(1, 'Brisbane', 'The first night.', 24 * 60, u('1494438639946-1ebd1d20bf85'), 'subscribed'),
      ep(2, 'Newcastle', 'A small room, a generous bartender.', 27 * 60, u('1494438639946-1ebd1d20bf85'), 'subscribed'),
      ep(3, 'Sydney', 'The smoke alarms went off.', 35 * 60, u('1494438639946-1ebd1d20bf85'), 'subscribed'),
      ep(4, 'Wollongong', 'A drive home that lasted a year.', 22 * 60, u('1494438639946-1ebd1d20bf85'), 'subscribed'),
    ],
  },
  {
    id: 's_off_the_leash',
    slug: 'off-the-leash',
    title: 'OFF THE LEASH',
    eyebrow: 'Locked drop',
    tagline: 'A book that became a series.',
    synopsis:
      'Mara Okeke\'s portrait series, expanded into ten short films. PPV unlock.',
    kind: 'series',
    rating: '18+',
    releasedAt: '2026-01-12',
    creatorIds: [byHandle('mara')],
    heroUrl: u('1534528741775-53994a69daeb', 2400),
    scene: 'mirror',
    sceneSeed: 14,
    tags: ['locked', 'portrait', 'editorial'],
    episodes: [
      ep(1, 'A', 'A motel hallway. A name, then a quiet.', 9 * 60, u('1534528741775-53994a69daeb'), 'locked'),
      ep(2, 'B', 'Studio 4. One wall, three lights, one hour.', 11 * 60, u('1492562080023-ab3db95bfbce'), 'locked'),
      ep(3, 'C', 'A late conversation about an old friend.', 14 * 60, u('1583454110551-21f2fa2afe61'), 'locked'),
    ],
  },
  {
    id: 's_heavyweight',
    slug: 'heavyweight',
    title: 'HEAVYWEIGHT',
    eyebrow: 'Originals',
    tagline: 'A loud room, in slow motion.',
    synopsis:
      'A four-part doc on the rise of a single small venue. Filmed over twelve months.',
    kind: 'series',
    rating: '15+',
    releasedAt: '2025-09-04',
    creatorIds: [byHandle('jude'), byHandle('kasper')],
    heroUrl: u('1488161628813-04466f872be2', 2400),
    scene: 'backstage',
    sceneSeed: 51,
    tags: ['original', 'doc', 'venue'],
    episodes: [
      ep(1, 'Open', 'The first night the door held.', 30 * 60, u('1488161628813-04466f872be2'), 'subscribed'),
      ep(2, 'Tight', 'Year one, no headliners.', 33 * 60, u('1488161628813-04466f872be2'), 'subscribed'),
      ep(3, 'Heavy', 'The breakthrough month.', 28 * 60, u('1488161628813-04466f872be2'), 'subscribed'),
      ep(4, 'Hold', 'The night that nearly closed it.', 36 * 60, u('1488161628813-04466f872be2'), 'subscribed'),
    ],
  },
  {
    id: 's_last_light',
    slug: 'last-light',
    title: 'LAST LIGHT',
    eyebrow: 'After hours',
    tagline: 'The hour before the bus comes.',
    synopsis:
      'Five short films set in the half hour after closing. Each one a different room.',
    kind: 'short',
    rating: '15+',
    releasedAt: '2025-12-19',
    creatorIds: [byHandle('iris'), byHandle('jude')],
    heroUrl: u('1478720568477-152d9b164e26', 2400),
    scene: 'haze',
    sceneSeed: 5,
    tags: ['after hours', 'short form', 'editorial'],
    episodes: [
      ep(1, 'Northcote', 'A cab driver waiting in the alley.', 7 * 60, u('1478720568477-152d9b164e26'), 'subscribed'),
      ep(2, 'Carlton', 'Two friends, one phone, one shared key.', 8 * 60, u('1478720568477-152d9b164e26'), 'subscribed'),
      ep(3, 'St Kilda', 'A boardwalk, a dog, a beer.', 6 * 60, u('1478720568477-152d9b164e26'), 'subscribed'),
    ],
  },
  {
    id: 's_back_door',
    slug: 'the-back-door',
    title: 'THE BACK DOOR',
    eyebrow: 'Locked drop',
    tagline: 'The exit nobody photographs.',
    synopsis:
      'Sebastian Roux\'s long-form portrait collection. Six photographic reels, scored to room tone.',
    kind: 'short',
    rating: '18+',
    releasedAt: '2026-04-02',
    creatorIds: [byHandle('sebastian')],
    heroUrl: u('1500648767791-00dcc994a43e', 2400),
    scene: 'flash',
    sceneSeed: 11,
    tags: ['locked', 'portrait', 'editorial'],
    episodes: [
      ep(1, 'I', 'Studio. One light. Forty-two frames.', 8 * 60, u('1500648767791-00dcc994a43e'), 'locked'),
      ep(2, 'II', 'Hotel room. Mirror. The longest take.', 11 * 60, u('1506794778202-cad84cf45f1d'), 'locked'),
      ep(3, 'III', 'Backstage. The minute before the set.', 7 * 60, u('1488161628813-04466f872be2'), 'locked'),
    ],
  },
  {
    id: 's_clean_down',
    slug: 'clean-down',
    title: 'CLEAN DOWN',
    eyebrow: 'New original',
    tagline: 'The hour the staff get the room back.',
    synopsis:
      'Six episodes on the people who run the venues. The bartenders, the door staff, the cleaners.',
    kind: 'documentary',
    rating: '15+',
    releasedAt: '2026-05-08',
    creatorIds: [byHandle('kasper')],
    heroUrl: u('1488161628813-04466f872be2', 2400),
    scene: 'backstage',
    sceneSeed: 28,
    tags: ['original', 'doc', 'staff'],
    episodes: [
      ep(1, 'The Bar', 'A bartender at 5am.', 20 * 60, u('1488161628813-04466f872be2'), 'free'),
      ep(2, 'The Door', 'A bouncer\'s slow night.', 24 * 60, u('1488161628813-04466f872be2'), 'subscribed'),
      ep(3, 'The Cleaner', 'The shift that begins as the last guest leaves.', 18 * 60, u('1488161628813-04466f872be2'), 'subscribed'),
    ],
  },

  /* ===== Performer-led + studio releases — Naked-Sword-meets-Netflix ===== */
  {
    id: 's_bedroom_tapes',
    slug: 'bedroom-tapes',
    title: 'BEDROOM TAPES',
    eyebrow: 'Performer series · Roman Saint',
    tagline: 'A camera, a hotel room, a long evening.',
    synopsis:
      'Six chapters from a single floor of a single hotel. Roman Saint, alone, talking to a camera he set up himself. 18+ throughout.',
    kind: 'series',
    rating: '18+',
    releasedAt: '2026-04-04',
    creatorIds: [byHandle('roman_saint'), byHandle('iris')],
    heroUrl: u('1500648767791-00dcc994a43e', 2400),
    scene: 'flash',
    sceneSeed: 3,
    tags: ['performer', 'scene', 'hotel cinema', 'long form'],
    episodes: [
      ep(1, 'Floor 12', 'Check-in. The light, the bed, the first hour.', 22 * 60, u('1500648767791-00dcc994a43e'), 'subscribed'),
      ep(2, 'Floor 14', 'A second night. A different room. The same camera angle.', 26 * 60, u('1568602471122-7832951cc4c5'), 'subscribed'),
      ep(3, 'Floor 16', 'A long late take. Spoken, mostly.', 31 * 60, u('1500648767791-00dcc994a43e'), 'locked'),
      ep(4, 'Floor 19', 'The penthouse cycle.', 38 * 60, u('1571266028243-d220c6a7d1ab'), 'locked'),
    ],
  },
  {
    id: 's_after_midnight',
    slug: 'after-midnight',
    title: 'AFTER MIDNIGHT',
    eyebrow: 'Studio cycle · Late Light',
    tagline: 'Scenes for the slow hours.',
    synopsis:
      'Late Light Studios\' first cycle. Six pairs, six rooms, one hour each. Cinematic adult cinema, restrained and looped.',
    kind: 'series',
    rating: '18+',
    releasedAt: '2026-03-22',
    creatorIds: [byHandle('late_light'), byHandle('alexei_kira'), byHandle('cassian_vega')],
    heroUrl: u('1568602471122-7832951cc4c5', 2400),
    scene: 'club_room',
    sceneSeed: 47,
    tags: ['studio', 'scene', 'after hours', 'editorial'],
    episodes: [
      ep(1, 'I · Carlton', 'Apartment 3B. Two friends. The longest night.', 41 * 60, u('1488161628813-04466f872be2'), 'subscribed'),
      ep(2, 'II · Fitzroy', 'A warehouse loft. One light, one shoot.', 38 * 60, u('1500648767791-00dcc994a43e'), 'subscribed'),
      ep(3, 'III · St Kilda', 'A motel by the boardwalk.', 44 * 60, u('1568602471122-7832951cc4c5'), 'locked'),
      ep(4, 'IV · Brunswick East', 'A studio rented for the weekend.', 36 * 60, u('1500648767791-00dcc994a43e'), 'locked'),
      ep(5, 'V · The Office', 'After-hours, by invitation.', 29 * 60, u('1488161628813-04466f872be2'), 'locked'),
      ep(6, 'VI · The Suite', 'The cycle, finished.', 47 * 60, u('1571266028243-d220c6a7d1ab'), 'locked'),
    ],
  },
  {
    id: 's_studio_seven',
    slug: 'studio-seven',
    title: 'STUDIO 7',
    eyebrow: 'Late Light · monthly drop',
    tagline: 'A different room. The same long takes.',
    synopsis:
      'A monthly scene cycle from Late Light Studios. Each drop is a single shoot day, a single roster, a single set.',
    kind: 'series',
    rating: '18+',
    releasedAt: '2026-05-01',
    creatorIds: [byHandle('late_light'), byHandle('kai_north')],
    heroUrl: u('1488161628813-04466f872be2', 2400),
    scene: 'backstage',
    sceneSeed: 13,
    tags: ['studio', 'scene', 'monthly drop', 'locked'],
    episodes: [
      ep(1, 'May · Studio 7', 'The May shoot. The late session.', 52 * 60, u('1488161628813-04466f872be2'), 'subscribed', '2026-05-22'),
      ep(2, 'April · Studio 7', 'The April drop, archived for subscribers.', 48 * 60, u('1500648767791-00dcc994a43e'), 'subscribed'),
      ep(3, 'March · Studio 7', 'A locked release. Three rooms.', 44 * 60, u('1568602471122-7832951cc4c5'), 'locked'),
      ep(4, 'February · Studio 7', 'A locked release. The double-shoot.', 51 * 60, u('1500648767791-00dcc994a43e'), 'locked'),
    ],
  },
  {
    id: 's_performer_files',
    slug: 'performer-files',
    title: 'PERFORMER FILES',
    eyebrow: 'Original portrait series',
    tagline: 'Five working performers. Five long evenings.',
    synopsis:
      'Mara Okeke directs. Cassian Vega, Roman Saint, Alexei Kira, Kai North, and a fifth name to drop later. Studio portraits with conversation between takes.',
    kind: 'short',
    rating: '18+',
    releasedAt: '2026-04-18',
    creatorIds: [byHandle('mara'), byHandle('cassian_vega'), byHandle('roman_saint'), byHandle('alexei_kira'), byHandle('kai_north')],
    heroUrl: u('1531891437562-4301cf35b7e4', 2400),
    scene: 'mirror',
    sceneSeed: 21,
    tags: ['performer', 'portrait', 'original', 'editorial'],
    episodes: [
      ep(1, 'Cassian', 'A studio. A long evening. The camera, eventually.', 27 * 60, u('1568602471122-7832951cc4c5'), 'subscribed'),
      ep(2, 'Roman', 'Hotel 19. The same room as last time. A different reason.', 31 * 60, u('1500648767791-00dcc994a43e'), 'subscribed'),
      ep(3, 'Alexei', 'A trained dancer rehearsing for nobody.', 24 * 60, u('1539571696357-5a69c17a67c6'), 'subscribed'),
      ep(4, 'Kai', 'A long flight, a short night, a longer take.', 33 * 60, u('1531891437562-4301cf35b7e4'), 'subscribed'),
    ],
  },
  {
    id: 's_room_service',
    slug: 'room-service',
    title: 'ROOM SERVICE',
    eyebrow: 'Locked drop · cinematic adult',
    tagline: 'Hotel cinema. Three rooms on the same floor.',
    synopsis:
      'A premium PPV cycle. Three rooms, three pairs, three lighting setups, no music. Available as a single unlock.',
    kind: 'short',
    rating: '18+',
    releasedAt: '2026-04-10',
    creatorIds: [byHandle('late_light'), byHandle('roman_saint'), byHandle('cassian_vega')],
    heroUrl: u('1568602471122-7832951cc4c5', 2400),
    scene: 'mirror',
    sceneSeed: 29,
    tags: ['locked', 'scene', 'studio', 'hotel cinema'],
    episodes: [
      ep(1, 'Room 14', 'A long take, low light, soft talk.', 28 * 60, u('1568602471122-7832951cc4c5'), 'locked'),
      ep(2, 'Room 16', 'The middle of the floor.', 32 * 60, u('1500648767791-00dcc994a43e'), 'locked'),
      ep(3, 'Room 19', 'The penthouse. The longest cut.', 41 * 60, u('1488161628813-04466f872be2'), 'locked'),
    ],
  },
  {
    id: 's_blackout',
    slug: 'blackout',
    title: 'BLACKOUT',
    eyebrow: 'New scene cycle',
    tagline: 'One light. One scene. One take.',
    synopsis:
      'A minimal, single-light scene cycle. No cuts in-shot. Twenty minutes per chapter. Cassian Vega directs and performs.',
    kind: 'short',
    rating: '18+',
    releasedAt: '2026-05-09',
    creatorIds: [byHandle('cassian_vega')],
    heroUrl: u('1500648767791-00dcc994a43e', 2400),
    scene: 'backstage',
    sceneSeed: 36,
    tags: ['scene', 'experimental', 'performer', 'short form'],
    episodes: [
      ep(1, 'Black 01', 'The first cut. Studio 4.', 21 * 60, u('1500648767791-00dcc994a43e'), 'subscribed', '2026-05-23'),
      ep(2, 'Black 02', 'The second cut. Studio 4. Same lamp.', 22 * 60, u('1488161628813-04466f872be2'), 'subscribed', '2026-05-30'),
      ep(3, 'Black 03', 'The locked cut.', 26 * 60, u('1568602471122-7832951cc4c5'), 'locked'),
    ],
  },
];

/* ============================================================
   COLLECTIONS — the rails on the Browse page
   ============================================================ */

export const mockTvCollections: TvCollection[] = [
  {
    id: 'col_premiere',
    slug: 'premiering-this-week',
    title: 'PREMIERING THIS WEEK',
    kicker: 'New tonight',
    seriesIds: ['s_studio_seven', 's_sweat_capital', 's_clean_down', 's_blackout', 's_room_tone'],
  },
  {
    id: 'col_new_scenes',
    slug: 'new-scenes-this-week',
    title: 'NEW SCENES THIS WEEK',
    kicker: 'Studio + performer drops',
    seriesIds: ['s_studio_seven', 's_blackout', 's_after_midnight', 's_bedroom_tapes'],
  },
  {
    id: 'col_studio_drops',
    slug: 'studio-drops',
    title: 'STUDIO DROPS',
    kicker: 'Late Light · cinematic adult',
    seriesIds: ['s_studio_seven', 's_after_midnight', 's_room_service'],
  },
  {
    id: 'col_locked',
    slug: 'locked-rooms',
    title: 'LOCKED ROOMS',
    kicker: 'Pay-per-view',
    seriesIds: ['s_room_service', 's_off_the_leash', 's_back_door', 's_blackout', 's_sweat_capital'],
  },
  {
    id: 'col_creators',
    slug: 'creators-worth-your-time',
    title: 'CREATORS WORTH YOUR TIME',
    seriesIds: ['s_bedroom_tapes', 's_mid_tempo', 's_performer_files', 's_off_the_leash', 's_after_the_headliners', 's_back_door'],
  },
  {
    id: 'col_originals',
    slug: 'originals',
    title: 'ORIGINALS',
    kicker: 'Curated by HOTTR editorial',
    seriesIds: ['s_mid_tempo', 's_performer_files', 's_after_the_headliners', 's_heavyweight', 's_clean_down'],
  },
  {
    id: 'col_after_hours',
    slug: 'after-hours',
    title: 'AFTER HOURS',
    seriesIds: ['s_after_midnight', 's_bedroom_tapes', 's_room_tone', 's_last_light', 's_after_the_headliners'],
  },
  {
    id: 'col_roster',
    slug: 'from-the-roster',
    title: 'FROM THE ROSTER',
    kicker: 'Working creators',
    seriesIds: ['s_bedroom_tapes', 's_performer_files', 's_off_the_leash', 's_back_door', 's_northbound', 's_heavyweight'],
  },
];

/* ============================================================
   LIBRARY — fake "current viewer" state
   ============================================================ */

export const mockLibrary: TvLibraryItem[] = [
  {
    id: 'lib_1',
    seriesId: 's_mid_tempo',
    episodeId: mockTvSeries[0].episodes[2].id,
    progress: 0.42,
    state: 'continue',
    at: Date.now() - 1000 * 60 * 60 * 2,
  },
  {
    id: 'lib_2',
    seriesId: 's_after_the_headliners',
    episodeId: mockTvSeries[1].episodes[0].id,
    progress: 0.81,
    state: 'continue',
    at: Date.now() - 1000 * 60 * 60 * 18,
  },
  {
    id: 'lib_3',
    seriesId: 's_room_tone',
    state: 'saved',
    at: Date.now() - 1000 * 60 * 60 * 24 * 3,
  },
  {
    id: 'lib_4',
    seriesId: 's_sweat_capital',
    state: 'saved',
    at: Date.now() - 1000 * 60 * 60 * 24 * 1,
  },
  {
    id: 'lib_5',
    seriesId: 's_off_the_leash',
    state: 'purchased',
    at: Date.now() - 1000 * 60 * 60 * 24 * 7,
  },
  {
    id: 'lib_6',
    seriesId: 's_heavyweight',
    state: 'history',
    at: Date.now() - 1000 * 60 * 60 * 24 * 14,
  },
];

/* ============================================================
   LOOKUPS
   ============================================================ */

export const findTvSeries = (slug: string): TvSeries | undefined =>
  mockTvSeries.find((s) => s.slug === slug);

export const findTvSeriesById = (id: string): TvSeries | undefined =>
  mockTvSeries.find((s) => s.id === id);

export const findTvCreator = (handle: string): TvCreator | undefined =>
  mockTvCreators.find((c) => c.handle === handle);

export const findTvCreatorById = (id: string): TvCreator | undefined =>
  mockTvCreators.find((c) => c.id === id);

/** Resolve an episode by id across all series. */
export const findTvEpisode = (
  id: string,
): { series: TvSeries; episode: TvEpisode } | undefined => {
  for (const series of mockTvSeries) {
    const episode = series.episodes.find((e) => e.id === id);
    if (episode) return { series, episode };
  }
  return undefined;
};
